import { ParsedUrlQuery } from 'querystring'
import { createFilterOptions } from '@mui/material/useAutocomplete'
import { Filter, onChangeFunc } from 'components/core/search-filter/filter'
import { getFilterKeyToLabelMap } from 'components/core/task/ContextualTaskSearchFilter'
import { appUserTypeToIntl, appUserTypeToString } from 'components/pages/user/type'
import { AppUserType } from 'pbts/c1/api/task/v1/search'
import { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'

export const AccountTypeQueryParam = 'accountType'

type AccountTypeFilterProps = {
    selected: AppUserType[] | undefined
    onChange: onChangeFunc<AppUserType>
}

const getOptionSelected = (option1: AppUserType, option2: AppUserType): boolean => {
    return option1 === option2
}

export const AccountTypeFilter = ({ selected, onChange }: AccountTypeFilterProps): React.ReactElement => {
    const intl = useIntl()
    const filterOptions = createFilterOptions<AppUserType>({})
    const filterKeyToLabel = getFilterKeyToLabelMap(intl)

    const accountTypes: AppUserType[] = useMemo(() => {
        const types: AppUserType[] = []
        for (const typ in AppUserType) {
            if ((AppUserType[typ] as unknown as number) > 0) {
                types.push(AppUserType[typ] as unknown as AppUserType)
            }
        }
        return types
    }, [])

    const renderLabel = useCallback((option: AppUserType): string => appUserTypeToIntl(option, intl), [intl])

    // Load initial types from query.
    const fromQuery = useCallback((query: ParsedUrlQuery): AppUserType[] => {
        let types = query[AccountTypeQueryParam]
        if (!types) {
            return []
        }

        if (!Array.isArray(types)) {
            types = [types]
        }

        const parsedTypes: AppUserType[] = []
        types.forEach((typeStr) => {
            const typ = AppUserType[typeStr]
            if (!typ) {
                return
            }
            parsedTypes.push(typ)
        })

        return parsedTypes
    }, [])

    // Serialize selections to query.
    const toQuery = useCallback((query: ParsedUrlQuery, types: AppUserType[]): void => {
        const typeStrs: string[] = []
        types.forEach((typ) => {
            const typeStr = appUserTypeToString(typ)
            if (!typeStr) {
                return
            }
            typeStrs.push(typeStr)
        })

        if (typeStrs.length !== 0) {
            query[AccountTypeQueryParam] = typeStrs
        } else {
            delete query[AccountTypeQueryParam]
        }
    }, [])

    return <Filter<AppUserType>
        label={filterKeyToLabel['account-type'] || ''}
        options={accountTypes}
        selected={selected}
        fromQuery={fromQuery}
        toQuery={toQuery}
        onChange={onChange}
        filterOptions={filterOptions}
        getOptionSelected={getOptionSelected}
        renderLabel={renderLabel}
    />
}
